import type { ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router';
import { usePageSection } from '@/hooks/usePageContent';
import type { LegalItem, LegalPageContent, LegalSection } from '@/types/pageContent';

interface LegalPageProps {
    pageSlug: string;
    title: string;
    /**
     * Kept on the props for back-compat with the route shells / feature
     * wrappers that still pass it through (it documents the page intent at
     * the call site), but it is no longer rendered here — SEO descriptors
     * now live in each route's `meta` export. Safe to drop later if a callsite
     * cleanup goes through.
     */
    metaDescription?: string;
    defaults: LegalPageContent;
}

function formatLastUpdated(dateStr: string | undefined): string | null {
    if (!dateStr) {
        return null;
    }

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return date.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
}

function renderBodyText(text: string): ReactNode {
    const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/;
    const parts = text.split(emailRegex);

    if (parts.length === 1) {
        return text;
    }

    return parts.map((part, index) => {
        if (emailRegex.test(part)) {
            return (
                <a key={index} href={`mailto:${part}`} className="text-primary hover:underline">
                    {part}
                </a>
            );
        }

        return part;
    });
}

function normalizeSection(section: unknown): LegalSection | null {
    if (!section || typeof section !== 'object') {
        return null;
    }

    const candidate = section as Partial<LegalSection>;

    if (typeof candidate.heading !== 'string' || !Array.isArray(candidate.items)) {
        return null;
    }

    const items = candidate.items
        .filter(
            (item): item is LegalItem =>
                !!item &&
                typeof item === 'object' &&
                typeof item.title === 'string' &&
                typeof item.body === 'string',
        );

    if (items.length !== candidate.items.length) {
        return null;
    }

    const style =
        candidate.style === 'default' || candidate.style === 'cards' || candidate.style === 'list'
            ? candidate.style
            : 'default';

    return {
        heading: candidate.heading,
        style,
        items,
    };
}

function normalizeContent(cms: LegalPageContent | null, defaults: LegalPageContent): LegalPageContent {
    if (!cms || !Array.isArray((cms as Partial<LegalPageContent>).sections)) {
        return defaults;
    }

    const sections = cms.sections
        .map((section) => normalizeSection(section))
        .filter((section): section is LegalSection => section !== null);

    if (sections.length === 0) {
        return defaults;
    }

    return {
        lastUpdated: typeof cms.lastUpdated === 'string' ? cms.lastUpdated : defaults.lastUpdated,
        sections,
    };
}

function DefaultStyleItems({ items, sectionIndex }: { items: LegalItem[]; sectionIndex: number }) {
    return (
        <div className="mt-4 space-y-4 text-muted-foreground">
            {items.map((item, itemIndex) => (
                <p key={`${sectionIndex}-${itemIndex}`}>
                    {item.title ? (
                        <>
                            <strong>
                                {sectionIndex + 1}.{itemIndex + 1} {item.title}:
                            </strong>{' '}
                            {renderBodyText(item.body)}
                        </>
                    ) : (
                        renderBodyText(item.body)
                    )}
                </p>
            ))}
        </div>
    );
}

function CardsStyleItems({ items }: { items: LegalItem[] }) {
    return (
        <div className="mt-4">
            <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {items.map((item, index) => (
                    <div key={`${item.title}-${index}`} className="rounded-lg border border-border bg-white p-4">
                        <dt className="font-medium text-foreground">{item.title}</dt>
                        <dd className="mt-1 text-sm text-muted-foreground">{renderBodyText(item.body)}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
}

function ListStyleItems({ items }: { items: LegalItem[] }) {
    return (
        <div className="mt-4 text-muted-foreground">
            <ul className="list-disc space-y-2 pl-5">
                {items.map((item, index) => (
                    <li key={`bullet-${index}`}>
                        {item.title ? (
                            <>
                                <strong>{item.title}:</strong> {renderBodyText(item.body)}
                            </>
                        ) : (
                            renderBodyText(item.body)
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
}

function SectionContent({
    section,
    sectionIndex,
    isLast,
}: {
    section: LegalSection;
    sectionIndex: number;
    isLast: boolean;
}) {
    return (
        <section className={isLast ? 'mb-10 rounded-lg bg-muted p-6' : 'mb-10'}>
            <h2 className={isLast ? 'text-lg font-semibold text-foreground' : 'text-xl font-semibold text-foreground'}>
                {sectionIndex + 1}. {section.heading}
            </h2>

            {section.style === 'cards' && <CardsStyleItems items={section.items} />}
            {section.style === 'list' && <ListStyleItems items={section.items} />}
            {section.style === 'default' && (
                <DefaultStyleItems items={section.items} sectionIndex={sectionIndex} />
            )}
        </section>
    );
}

export const LegalPage = ({ pageSlug, title, defaults }: LegalPageProps) => {
    const cms = usePageSection<LegalPageContent>(pageSlug, 'content');
    const content = normalizeContent(cms, defaults);
    const formattedDate = formatLastUpdated(content.lastUpdated);

    return (
        <>
            <div className="bg-white py-12 sm:py-16">
                <div className="mx-auto max-w-3xl px-6 lg:px-8">
                    <div className="mb-8">
                        <Link
                            to="/"
                            className="inline-flex items-center text-sm font-medium text-primary hover:underline"
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Back to Home
                        </Link>
                    </div>

                    <div className="mb-12">
                        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                            {title}
                        </h1>
                        {formattedDate && (
                            <p className="mt-3 text-sm text-muted-foreground">Last updated: {formattedDate}</p>
                        )}
                    </div>

                    <div className="prose prose-slate max-w-none">
                        {content.sections.map((section, index) => (
                            <SectionContent
                                key={`${section.heading}-${index}`}
                                section={section}
                                sectionIndex={index}
                                isLast={index === content.sections.length - 1}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
};

export default LegalPage;
